import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { settings } from "../../helpers/settings";
import authHeader from "../../helpers/functions/auth-header";
import { toast } from "../../helpers/swal";
import { AiOutlineHome } from "react-icons/ai";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import SyncProgressBar from "./SyncProgressBar";

// Lottie
import Lottie from "lottie-react";
import printingAnimation from "../../helpers/animations/printing.json";

dayjs.extend(utc);
dayjs.extend(timezone);

const CreateGangsheet = () => {
  const [orders, setOrders] = useState([]);
  const [allOrders, setAllOrders] = useState([]);
  const [selectedShop, setSelectedShop] = useState("");
  const [shops, setShops] = useState([]);
  const [notes, setNotes] = useState({});
  const [statusFilter, setStatusFilter] = useState("awaiting_shipment");
  const [summary, setSummary] = useState({ todayCount: 0 });
  const [searchQuery, setSearchQuery] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [tags, setTags] = useState([]);
  const [fetchingAll, setFetchingAll] = useState(false);
  const navigate = useNavigate();
  const [syncing, setSyncing] = useState(false);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [logSteps, setLogSteps] = useState([]);

  const [syncProgress, setSyncProgress] = useState({ current: 0, total: 0 });
  const [syncSummary, setSyncSummary] = useState(null);
  const [syncMessage, setSyncMessage] = useState(null);

  // Lottie overlay kontrolü
  const [isGenerating, setIsGenerating] = useState(false);

  // Filtre durumları (başlıklardaki dropdown’lar)
  const [isPlacedFilter, setIsPlacedFilter] = useState("all"); // all | yes | no
  const [uploadedFilter, setUploadedFilter] = useState("all"); // all | yes | no
  const [selectedTagIds, setSelectedTagIds] = useState([]); // [] => hepsi

  // Dropdown açık/kapalı
  const [openIsPlaced, setOpenIsPlaced] = useState(false);
  const [openUploaded, setOpenUploaded] = useState(false);
  const [openTags, setOpenTags] = useState(false);

  const isPlacedRef = useRef(null);
  const uploadedRef = useRef(null);
  const tagsRef = useRef(null);

  const NO_TAGS = "__NO_TAGS__";

  useEffect(() => {
    fetchShops();
  }, []);

  useEffect(() => {
    axios
      .get(`${settings.apiURL}/get_shipstation_tags.php`, {
        headers: authHeader(),
      })
      .then((res) => setTags(res.data.tags || []))
      .catch(() => setTags([]));
  }, []);

  // Filtreleme: arama + üç filtre birlikte
  useEffect(() => {
    applyFilters();
    // eslint-disable-next-line
  }, [searchQuery, allOrders, isPlacedFilter, uploadedFilter, selectedTagIds]);

  useEffect(() => {
    if (sortConfig.key) {
      sortOrders(sortConfig.key, sortConfig.direction);
    }
  }, [sortConfig]);

  useEffect(() => {
    if (selectedShop) {
      getFromCacheOnly(selectedShop);
    }
  }, [selectedShop, statusFilter]);

  // Üretim sırasında body scroll'u kapat
  useEffect(() => {
    const original = document.body.style.overflow;
    if (isGenerating) document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = original;
    };
  }, [isGenerating]);

  // Dropdown’lar için dış tıklama kapatma
  useEffect(() => {
    const onDown = (e) => {
      if (
        openIsPlaced &&
        isPlacedRef.current &&
        !isPlacedRef.current.contains(e.target)
      ) {
        setOpenIsPlaced(false);
      }
      if (
        openUploaded &&
        uploadedRef.current &&
        !uploadedRef.current.contains(e.target)
      ) {
        setOpenUploaded(false);
      }
      if (openTags && tagsRef.current && !tagsRef.current.contains(e.target)) {
        setOpenTags(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [openIsPlaced, openUploaded, openTags]);

  const fetchShops = () => {
    axios
      .get(`${settings.apiURL}/admin_get_all_shops.php`, {
        headers: authHeader(),
      })
      .then((res) => {
        if (res.data.success) {
          setShops(res.data.shops);
          const savedShopId = localStorage.getItem("adminLastShop");
          if (
            savedShopId &&
            res.data.shops.some((s) => s.shop_id === savedShopId)
          ) {
            setSelectedShop(savedShopId);
          } else {
            setSelectedShop("all");
          }
        }
      })
      .catch(() => console.error("Failed to fetch shops"));
  };

  const fetchOrdersSmart = async () => {
    setFetchingAll(true);
    setSyncMessage("🔄 Refreshing cache...");
    setLogSteps([]);

    try {
      const response = await fetch(
        `${settings.apiURL}/refresh_order_cache.php`,
        {
          method: "GET",
          headers: authHeader(),
        }
      );

      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");

      let lines = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        lines += chunk;

        const chunks = lines.split("\n");
        lines = chunks.pop(); // incomplete line

        for (const line of chunks) {
          try {
            const parsed = JSON.parse(line);

            if (parsed.step) {
              setSyncMessage(parsed.step);
              setLogSteps((prev) => [...prev, parsed.step]);
            }
          } catch {
            /* noop */
          }
        }
      }

      setSyncMessage("📦 Loading cached orders...");

      const cachedRes = await axios.get(
        `${settings.apiURL}/get_cached_orders.php`,
        {
          headers: authHeader(),
        }
      );

      if (cachedRes.data.success && Array.isArray(cachedRes.data.orders)) {
        const enriched = enrichOrders(cachedRes.data.orders);
        setOrders(enriched);
        setAllOrders(enriched);
        countToday(enriched);
        setSyncMessage(null);
      } else {
        toast({ title: "No orders found in cache", icon: "info" });
        setOrders([]);
        setAllOrders([]);
        setSyncMessage(null);
      }
    } catch (error) {
      toast({ title: "Fetch failed", icon: "error" });
      setSyncMessage(null);
    }

    setFetchingAll(false);
  };

  const getFromCacheOnly = async (shopId = null) => {
    setFetchingAll(true);
    setSyncMessage("📦 Loading cached orders...");

    const url = shopId
      ? `${settings.apiURL}/get_cached_orders.php?store_id=${shopId}`
      : `${settings.apiURL}/get_cached_orders.php`;

    try {
      const res = await axios.get(url, {
        headers: authHeader(),
      });

      if (res.data.success && Array.isArray(res.data.orders)) {
        const enriched = enrichOrders(res.data.orders);
        setOrders(enriched);
        setAllOrders(enriched);
        countToday(enriched);
      } else {
        toast({ title: "No cached orders", icon: "info" });
        setOrders([]);
        setAllOrders([]);
      }
    } catch (error) {
      console.error("Cache load error", error);
      toast({ title: "Cache fetch failed", icon: "error" });
    }

    setSyncMessage(null);
    setFetchingAll(false);
  };

  const enrichOrders = (orders) => {
    return orders.map((order) => {
      const baseDate = order.orderDate || order.createDate;
      return {
        ...order,
        baseDate,
        age: calculateAge(baseDate),
        rawAgeHours: calculateRawAge(baseDate),
        shopName: order.shopName || "No Shop Name", // Shop name default
        customerName: order.shipTo?.name || "No Customer Name", // Customer name default
        requestedShippingService:
          order.requestedShippingService || "No Shipping Service", // Requested Service default
      };
    });
  };

  const countToday = (orderList) => {
    const today = dayjs().format("YYYY-MM-DD");
    const todayCount = orderList.filter((o) =>
      (o.baseDate || "").startsWith(today)
    ).length;
    setSummary({ todayCount });
  };

  const calculateAge = (createDate) => {
    const created = dayjs.tz(createDate, "America/Chicago");
    theNow();
    const now = dayjs().tz("America/Chicago");
    const diffInHours = now.diff(created, "hour");
    if (diffInHours < 24) return `${diffInHours}h`;
    const days = Math.floor(diffInHours / 24);
    const hours = diffInHours % 24;
    return `${days}d ${hours}h`;
  };

  const theNow = () => {}; // placeholder to avoid accidental extra deps

  const calculateRawAge = (createDate) => {
    const created = dayjs.tz(createDate, "America/Chicago");
    const now = dayjs().tz("America/Chicago");
    return now.diff(created, "hour");
  };

  const getAgeCellStyle = (hours) => {
    if (hours < 12) return { backgroundColor: "#d4edda", color: "#155724" };
    if (hours < 24) return { backgroundColor: "#fff3cd", color: "#856404" };
    return { backgroundColor: "#f8d7da", color: "#721c24" };
  };

  // Tüm filtreleri birlikte uygula
  const applyFilters = () => {
    let filtered = [...allOrders];

    // Search
    if (searchQuery.trim() !== "") {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (o) =>
          o.orderNumber?.toLowerCase().includes(q) ||
          o.shipTo?.name?.toLowerCase().includes(q)
      );
    }

    // Is Placed
    if (isPlacedFilter !== "all") {
      const want = isPlacedFilter === "yes";
      filtered = filtered.filter((o) => !!o.is_placed === want);
    }

    // Order Uploaded
    if (uploadedFilter !== "all") {
      const want = uploadedFilter === "yes";
      filtered = filtered.filter((o) => !!o.design_uploaded === want);
    }

    // Tags: ANY match; boşsa hepsi
    // Tags: ANY match; boşsa hepsi
    if (selectedTagIds.length > 0) {
      const wanted = new Set(selectedTagIds); // string set
      const includeNoTags = wanted.has(NO_TAGS);

      filtered = filtered.filter((o) => {
        const ids = (o.tagIds || []).map(String);
        const hasAnySelectedTag = ids.some((id) => wanted.has(id));
        const hasNoTags = ids.length === 0;

        // Seçili taglerden en az birine sahip OLMASI veya
        // "No Tags" seçili ise ve kaydın tag'i olmaması
        return hasAnySelectedTag || (includeNoTags && hasNoTags);
      });
    }

    setOrders(filtered);
  };

  // Sıralama
  const sortOrders = (key, direction) => {
    const sorted = [...orders].sort((a, b) => {
      let valA, valB;

      if (key === "tags") {
        valA = a.tagIds?.[0] || "";
        valB = b.tagIds?.[0] || "";
      } else if (key === "shopName") {
        valA = a.shopName?.toLowerCase() || "";
        valB = b.shopName?.toLowerCase() || "";
      } else if (key === "requestedShippingService") {
        valA = a.requestedShippingService?.toLowerCase() || "";
        valB = b.requestedShippingService?.toLowerCase() || "";
      } else if (key === "internalNotes") {
        valA = a.internalNotes?.toLowerCase() || "";
        valB = b.internalNotes?.toLowerCase() || "";
      } else if (key === "is_placed") {
        valA = a.is_placed ? 1 : 0;
        valB = b.is_placed ? 1 : 0;
      } else if (key === "design_uploaded") {
        valA = a.design_uploaded ? 1 : 0;
        valB = b.design_uploaded ? 1 : 0;
      } else if (key === "orderNumber") {
        valA = a.orderNumber?.toLowerCase() || "";
        valB = b.orderNumber?.toLowerCase() || "";
      } else if (key === "shipTo.name") {
        valA = a.shipTo?.name?.toLowerCase() || "";
        valB = b.shipTo?.name?.toLowerCase() || "";
      } else if (key === "age") {
        valA = a.age || 0;
        valB = b.age || 0;
      } else if (key === "createDate") {
        valA = new Date(a.createDate) || new Date();
        valB = new Date(b.createDate) || new Date();
      } else {
        valA = typeof a[key] === "string" ? a[key].toLowerCase() : a[key];
        valB = typeof b[key] === "string" ? b[key].toLowerCase() : b[key];
      }

      if (valA < valB) return direction === "asc" ? -1 : 1;
      if (valA > valB) return direction === "asc" ? 1 : -1;
      return 0;
    });

    setOrders(sorted);
  };

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const handleNoteChange = (orderId, value) => {
    setNotes((prev) => ({ ...prev, [orderId]: value }));
  };

  const handleNoteBlur = (order) => {
    const updatedNote = notes[order.orderId];
    if (updatedNote === order.internalNotes) return;

    axios
      .post(
        `${settings.apiURL}/update_order_notes.php`,
        { orderId: order.orderId, internalNote: updatedNote },
        { headers: authHeader() }
      )
      .then((res) => {
        if (res.data.success) {
          toast({ title: "Note updated", icon: "success" });
          fetchOrdersSmart();
        } else {
          toast({ title: "Update failed", icon: "error" });
        }
      })
      .catch(() => {
        toast({ title: "An error occurred", icon: "error" });
      });
  };

  const SyncMessageBar = ({ message }) => {
    if (!message) return null;
    return (
      <div className="alert alert-info text-center py-2" role="alert">
        {message}
      </div>
    );
  };

  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedOrders([]);
    } else {
      setSelectedOrders(orders.map((o) => o.orderId));
    }
    setSelectAll(!selectAll);
  };

  const toggleSelectOne = (orderId) => {
    if (selectedOrders.includes(orderId)) {
      setSelectedOrders(selectedOrders.filter((id) => id !== orderId));
    } else {
      setSelectedOrders([...selectedOrders, orderId]);
    }
  };

  const handleCreateGangsheet = async () => {
    if (selectedOrders.length === 0) {
      toast({ title: "Please select at least one order", icon: "error" });
      return;
    }

    setIsGenerating(true);

    try {
      toast({ title: "Generating combined image...", icon: "info" });

      const response = await axios.post(
        `${settings.apiURL}/gen_order_img_v2.php`,
        { orderIds: selectedOrders },
        {
          headers: { ...authHeader(), "Content-Type": "application/json" },
          responseType: "blob",
        }
      );

      if (response.data instanceof Blob && response.data.size > 0) {
        if (response.data.type === "text/html") {
          const reader = new FileReader();
          reader.onload = () => {
            console.log("HTML Error Response:", reader.result);
            toast({ title: "Server returned HTML error page", icon: "error" });
          };
          reader.readAsText(response.data);
          return;
        }

        const url = window.URL.createObjectURL(response.data);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `${new Date().toISOString().split("T")[0]}.png`
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        toast({
          title: "Combined image generated successfully!",
          icon: "success",
        });
        // Sayfayı yenile ve güncel verileri getir
        setSelectedOrders([]);
        setSelectAll(false);
        await getFromCacheOnly(selectedShop);
      } else {
        const reader = new FileReader();
        reader.onload = () => {
          try {
            const errorData = JSON.parse(reader.result);
            toast({
              title: `Error: ${errorData.message || "Unknown error"}`,
              icon: "error",
            });
          } catch {
            toast({
              title: "Failed to generate combined image - invalid response",
              icon: "error",
            });
          }
        };
        reader.readAsText(response.data);
      }
    } catch (err) {
      console.error("Error details:", err);
      if (err.code === "ECONNABORTED") {
        toast({
          title:
            "Request timed out. Please try with fewer orders or contact support.",
          icon: "error",
        });
      } else if (err.response) {
        if (err.response.data instanceof Blob) {
          const reader = new FileReader();
          reader.onload = () => {
            try {
              const errorData = JSON.parse(reader.result);
              toast({
                title: `Server Error: ${errorData.message || "Unknown error"}`,
                icon: "error",
              });
            } catch {
              toast({
                title: `Server Error: ${err.response.status} - ${reader.result}`,
                icon: "error",
              });
            }
          };
          reader.readAsText(err.response.data);
        } else {
          toast({
            title: `Server Error: ${err.response.status} - ${err.response.statusText}`,
            icon: "error",
          });
        }
      } else if (err.request) {
        toast({
          title: "No response from server. Please check your connection.",
          icon: "error",
        });
      } else {
        toast({ title: `Error: ${err.message}`, icon: "error" });
      }
    } finally {
      setIsGenerating(false);
    }
  };

  // Shop değişince filtreleri sıfırla (hepsi)
  const handleShopChange = (e) => {
    const newShopId = e.target.value;
    setSelectedShop(newShopId);
    localStorage.setItem("adminLastShop", newShopId);
    setIsPlacedFilter("all");
    setUploadedFilter("all");
    setSelectedTagIds([]);
    setOpenIsPlaced(false);
    setOpenUploaded(false);
    setOpenTags(false);
  };

  // Özet (filtrelenmiş listede)
  const uploadedCount = orders.filter((o) => !!o.design_uploaded).length;
  const placedCount = orders.filter((o) => !!o.is_placed).length;

  // Dropdown ortak stilleri
  const caretBtn = {
    border: "none",
    background: "transparent",
    padding: "2px 4px",
    marginLeft: 6,
    cursor: "pointer",
    lineHeight: 1,
  };
  const dropdownStyle = {
    position: "absolute",
    top: "100%",
    right: 0,
    background: "#fff",
    border: "1px solid rgba(0,0,0,0.15)",
    borderRadius: 6,
    boxShadow: "0 8px 20px rgba(0,0,0,0.15)",
    padding: 8,
    zIndex: 50,
    minWidth: 160,
  };

  return (
    <>
      <SyncMessageBar message={syncMessage} />
      <SyncProgressBar progress={syncProgress} />
      {syncSummary && (
        <div className="alert alert-success my-3">
          <strong>Sync Completed:</strong>
          <br />
          Updated: {syncSummary.updated_total}
          <br />
          Shipped: {syncSummary.shipped} | Cancelled: {syncSummary.cancelled} |
          On Hold: {syncSummary.on_hold} | Unknown: {syncSummary.unknown} |
          Other: {syncSummary.other_status}
        </div>
      )}

      {/* Lottie Overlay */}
      {isGenerating && (
        <div
          role="dialog"
          aria-modal="true"
          style={overlayStyle}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={modalStyle}>
            <Lottie
              animationData={printingAnimation}
              loop
              autoplay
              style={{ width: 280, height: 280 }}
            />
            <div style={{ marginTop: 8, fontWeight: 600 }}>
              Preparing gangsheet…
            </div>
          </div>
        </div>
      )}

      <div className="container-fluid mt-4">
        {/* Home button */}
        <button
          className="btn btn-outline-secondary"
          onClick={() => navigate("/home")}
          style={{
            position: "absolute",
            top: "45px",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 9,
          }}
          disabled={isGenerating}
        >
          <AiOutlineHome className="me-2" /> Home
        </button>

        {/* Top controls */}
        <div
          className="d-flex justify-content-start align-items-center mb-3 flex-wrap"
          style={{ paddingTop: "80px" }}
        >
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <button
              className="btn btn-success"
              disabled={selectedOrders.length === 0 || isGenerating}
              onClick={handleCreateGangsheet}
            >
              {isGenerating ? "Creating…" : "Create Gangsheet"}
            </button>

            <button
              className="btn btn-primary"
              disabled={fetchingAll || syncing || isGenerating}
              onClick={() => {
                fetchOrdersSmart(true);
              }}
            >
              Fetch All Orders
            </button>

            <select
              className="form-select w-auto"
              value={selectedShop}
              disabled={fetchingAll || syncing || isGenerating}
              onChange={handleShopChange}
            >
              <option value="all">All Shops</option>
              {shops.map((shop) => (
                <option key={shop.shop_id} value={shop.shop_id}>
                  {shop.shop_name}
                </option>
              ))}
            </select>

            <input
              type="text"
              className="form-control w-auto"
              placeholder="Search order # or name"
              value={searchQuery}
              disabled={isGenerating}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        {/* Özet */}
        <div className="mb-3">
          <strong>Total Orders:</strong> {orders.length}
          <span className="ms-2">• Uploaded: {uploadedCount}</span>
          <span className="ms-2">• Is Placed: {placedCount}</span>
        </div>

        <table className="table table-bordered">
          <thead>
            <tr>
              <th style={{ width: 70 }}>
                <input
                  type="checkbox"
                  checked={selectAll}
                  onChange={toggleSelectAll}
                  style={{ marginRight: "6px" }}
                  disabled={isGenerating}
                />
                #
              </th>

              <th
                onClick={() => requestSort("orderNumber")}
                style={{ cursor: "pointer", minWidth: 140 }}
              >
                PO Number
              </th>

              {/* Shop biraz daraltıldı */}
              <th
                onClick={() => requestSort("shopName")}
                style={{ cursor: "pointer", minWidth: 140, maxWidth: 180 }}
              >
                Shop
              </th>

              {/* Customer Name biraz daraltıldı */}
              <th
                onClick={() => requestSort("shipTo.name")}
                style={{ cursor: "pointer", minWidth: 180, maxWidth: 240 }}
              >
                Customer Name
              </th>

              {/* Requested Service ciddi daraltıldı */}
              <th
                onClick={() => requestSort("requestedShippingService")}
                style={{ cursor: "pointer", width: 140, maxWidth: 160 }}
                title="Requested Service"
              >
                Requested Service
              </th>

              {/* Tags başlığına dropdown */}
              <th
                onClick={() => requestSort("tags")}
                style={{
                  cursor: "pointer",
                  position: "relative",
                  minWidth: 180,
                }}
                ref={tagsRef}
              >
                <div className="d-flex align-items-center justify-content-between">
                  <span>Tags</span>
                  <button
                    type="button"
                    style={caretBtn}
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenTags((v) => !v);
                      setOpenIsPlaced(false);
                      setOpenUploaded(false);
                    }}
                    title="Filter Tags"
                  >
                    ▾
                  </button>
                </div>

                {openTags && (
                  <div style={dropdownStyle}>
                    <div
                      style={{
                        maxHeight: 240,
                        overflowY: "auto",
                        paddingRight: 4,
                      }}
                    >
                      {/* No Tags seçeneği */}
                      <label
                        className="d-flex align-items-center"
                        style={{
                          gap: 8,
                          padding: "4px 6px",
                          cursor: "pointer",
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          checked={selectedTagIds.includes(NO_TAGS)}
                          onChange={(e) => {
                            const next = e.target.checked
                              ? [...selectedTagIds, NO_TAGS]
                              : selectedTagIds.filter((x) => x !== NO_TAGS);
                            setSelectedTagIds(next);
                          }}
                        />
                        <span>No Tags</span>
                      </label>

                      {tags.length === 0 && (
                        <div style={{ padding: "4px 6px", color: "#666" }}>
                          No tags
                        </div>
                      )}
                      {tags.map((t) => {
                        const id = String(t.tagId);
                        const checked = selectedTagIds.includes(id);
                        return (
                          <label
                            key={id}
                            className="d-flex align-items-center"
                            style={{
                              gap: 8,
                              padding: "4px 6px",
                              cursor: "pointer",
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(e) => {
                                const next = e.target.checked
                                  ? [...selectedTagIds, id]
                                  : selectedTagIds.filter((x) => x !== id);
                                setSelectedTagIds(next);
                              }}
                            />
                            <span>{t.name}</span>
                          </label>
                        );
                      })}
                    </div>
                    <div className="d-flex justify-content-between mt-2">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedTagIds([]);
                        }}
                      >
                        Clear
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenTags(false);
                        }}
                      >
                        Close
                      </button>
                    </div>
                  </div>
                )}
              </th>

              {/* Age */}
              <th
                onClick={() => requestSort("rawAgeHours")}
                style={{ cursor: "pointer", width: 100 }}
              >
                Age
              </th>

              <th
                onClick={() => requestSort("internalNotes")}
                style={{ cursor: "pointer", minWidth: 220 }}
              >
                Internal Note
              </th>

              {/* Is Placed genişletildi + dropdown */}
              <th
                onClick={() => requestSort("is_placed")}
                style={{ cursor: "pointer", position: "relative", width: 150 }}
                ref={isPlacedRef}
              >
                <div className="d-flex align-items-center justify-content-between">
                  <span>Is Placed</span>
                  <button
                    type="button"
                    style={caretBtn}
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenIsPlaced((v) => !v);
                      setOpenUploaded(false);
                      setOpenTags(false);
                    }}
                    title="Filter Is Placed"
                  >
                    ▾
                  </button>
                </div>

                {openIsPlaced && (
                  <div style={dropdownStyle}>
                    {["all", "yes", "no"].map((opt) => (
                      <button
                        key={opt}
                        type="button"
                        className="btn btn-sm w-100 text-start"
                        style={{
                          background:
                            isPlacedFilter === opt
                              ? "rgba(0,0,0,0.05)"
                              : "transparent",
                          marginBottom: 4,
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsPlacedFilter(opt);
                          setOpenIsPlaced(false);
                        }}
                      >
                        {opt === "all" ? "All" : opt === "yes" ? "Yes" : "No"}
                      </button>
                    ))}
                  </div>
                )}
              </th>

              {/* Order Uploaded genişletildi + dropdown */}
              <th
                onClick={() => requestSort("design_uploaded")}
                style={{ cursor: "pointer", position: "relative", width: 170 }}
                ref={uploadedRef}
              >
                <div className="d-flex align-items-center justify-content-between">
                  <span>Order Uploaded</span>
                  <button
                    type="button"
                    style={caretBtn}
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenUploaded((v) => !v);
                      setOpenIsPlaced(false);
                      setOpenTags(false);
                    }}
                    title="Filter Order Uploaded"
                  >
                    ▾
                  </button>
                </div>

                {openUploaded && (
                  <div style={dropdownStyle}>
                    {["all", "yes", "no"].map((opt) => (
                      <button
                        key={opt}
                        type="button"
                        className="btn btn-sm w-100 text-start"
                        style={{
                          background:
                            uploadedFilter === opt
                              ? "rgba(0,0,0,0.05)"
                              : "transparent",
                          marginBottom: 4,
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setUploadedFilter(opt);
                          setOpenUploaded(false);
                        }}
                      >
                        {opt === "all" ? "All" : opt === "yes" ? "Yes" : "No"}
                      </button>
                    ))}
                  </div>
                )}
              </th>
            </tr>
          </thead>

          <tbody>
            {orders.map((order, index) => (
              <tr key={order.orderId}>
                <td>
                  <input
                    type="checkbox"
                    checked={selectedOrders.includes(order.orderId)}
                    onChange={() => toggleSelectOne(order.orderId)}
                    style={{ marginRight: "6px" }}
                    disabled={isGenerating}
                  />
                  {index + 1}
                </td>

                <td>
                  <span
                    style={{
                      color: "blue",
                      cursor: "pointer",
                      textDecoration: "underline",
                      whiteSpace: "nowrap",
                    }}
                    onClick={() =>
                      !isGenerating &&
                      navigate(`/admin-order-details/${order.orderId}`, {
                        state: { orders, selectedShop },
                      })
                    }
                  >
                    {order.orderNumber}
                  </span>
                </td>

                <td
                  style={{
                    whiteSpace: "nowrap",
                    maxWidth: 180,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {order.shopName}
                </td>

                <td
                  style={{
                    whiteSpace: "nowrap",
                    maxWidth: 240,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {order.shipTo?.name || ""}
                </td>

                <td
                  style={{
                    whiteSpace: "nowrap",
                    maxWidth: 160,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {order.requestedShippingService || ""}
                </td>

                <td
                  style={{
                    maxWidth: 280,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {order.tagIds?.length > 0
                    ? order.tagIds
                        .map((id) => {
                          const tag = tags.find(
                            (t) => String(t.tagId) === String(id)
                          );
                          return tag?.name || null;
                        })
                        .filter(Boolean)
                        .join(", ")
                    : ""}
                </td>

                <td
                  style={{
                    ...getAgeCellStyle(order.rawAgeHours),
                    textAlign: "center",
                    whiteSpace: "nowrap",
                  }}
                >
                  {order.age}
                </td>

                <td>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={notes[order.orderId] || ""}
                    onChange={(e) =>
                      handleNoteChange(order.orderId, e.target.value)
                    }
                    onBlur={() => handleNoteBlur(order)}
                    disabled={isGenerating}
                  />
                </td>

                <td style={{ textAlign: "center", fontSize: 18 }}>
                  {order.is_placed ? "✅" : "❌"}
                </td>
                <td style={{ textAlign: "center", fontSize: 18 }}>
                  {order.design_uploaded ? "✅" : "❌"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

const overlayStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.35)", // hafif karartma
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 99999,
  pointerEvents: "auto",
};

const modalStyle = {
  background: "white",
  borderRadius: 12,
  padding: "24px 28px",
  boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  minWidth: 320,
};

export default CreateGangsheet;
